// PRODUCTION — Formik-bound number input
import React from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'

export interface ProtoNumberInputProps<T> {
  name: keyof T & string
  min?: number
  disabled?: boolean
}

const stepperBtnCss = (disabled: boolean) => [
  tw`flex h-[38px] w-9 items-center justify-center border-0 bg-transparent text-lg font-normal text-shades-500 transition-colors border-solid`,
  disabled ? tw`cursor-not-allowed` : tw`cursor-pointer hover:bg-shades-50 hover:text-shades-900`,
]

export const ProtoNumberInput = <T extends object>({
  name,
  min = 0,
  disabled = false,
}: ProtoNumberInputProps<T>) => {
  const { values, setFieldValue } = useFormikContext<T>()
  const value = values[name] as unknown as number

  const update = (next: number) => {
    if (disabled) {
      return
    }

    if (next >= min) {
      setFieldValue(name, next)
    }
  }

  return (
    <div
      css={[
        tw`flex h-[38px] flex-1 items-center overflow-hidden rounded-lg border border-solid border-shades-200 focus-within:border-primary-400`,
        disabled ? tw`bg-shades-50 opacity-[0.72]` : undefined,
      ]}
    >
      <input
        type="number"
        value={value}
        min={min}
        disabled={disabled}
        onChange={(e) => update(Number(e.target.value))}
        css={[
          tw`h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm outline-none border-solid`,
          disabled ? tw`cursor-not-allowed text-shades-500` : tw`cursor-text text-shades-900`,
        ]}
      />
      <div css={tw`flex flex-shrink-0 border-l border-shades-200 border-solid`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => update(value - 1)}
          css={stepperBtnCss(disabled)}
        >
          −
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => update(value + 1)}
          css={[stepperBtnCss(disabled), tw`border-l border-shades-200 border-solid`]}
        >
          +
        </button>
      </div>
    </div>
  )
}
